import { useEffect, useState } from 'react'
import { useAppStore } from '../../store/app'
import {
  listBlueprints,
  materializeBlueprint,
  type BlueprintNeed,
  type BlueprintParam,
} from '../../api/ws'
import './NewAgentWizard.css'
import './BlueprintPickerSheet.css'

// Start from a pattern somebody already worked out (docs/blueprints.md).
//
// Two steps, and the second only appears when it has to: pick a blueprint, then
// answer whatever the household could not answer for itself. Most households
// with one computer and one runtime go straight from picking to reviewing —
// the questions exist for the cases where guessing would be the system deciding
// something it was not asked to.
//
// It always ends on the ordinary review screen. A blueprint proposes; a person
// still approves every item, exactly as they would for one the Facilitator
// reasoned out.

// A required question with NO options cannot be answered here: the household has
// to go and set the thing up first. Surfacing which one — with the blueprint
// author's own setup steps — beats showing an empty dropdown.
export function unanswerableNeeds(needs: BlueprintNeed[]): BlueprintNeed[] {
  return needs.filter((n) => !n.optional && (n.options?.length ?? 0) === 0)
}

// Ready when every answerable question has an answer. An OPTIONAL need may be
// left blank on purpose (the items depending on it are dropped), and a need with
// no options is handled by unanswerableNeeds — treating it as unanswered would
// disable the button with nothing a person could do about it.
export function allAnswered(
  needs: BlueprintNeed[],
  params: BlueprintParam[],
  answers: Record<string, string>,
  paramAnswers: Record<string, string>,
): boolean {
  return (
    needs.every(
      (n) => n.optional || !!answers[n.alias] || (n.options?.length ?? 0) === 0,
    ) && params.every((p) => (paramAnswers[p.name] ?? '').trim() !== '')
  )
}

type Row = {
  slug: string
  version: string
  display_name: string
  summary: string
  tags?: string[]
}

export function BlueprintPickerSheet() {
  const popSheet = useAppStore((s) => s.popSheet)
  const pushNav = useAppStore((s) => s.pushNav)
  const organizationID = useAppStore((s) => s.organizationID)

  const [rows, setRows] = useState<Row[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [chosen, setChosen] = useState<Row | null>(null)
  const [needs, setNeeds] = useState<BlueprintNeed[]>([])
  const [params, setParams] = useState<BlueprintParam[]>([])
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [paramAnswers, setParamAnswers] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!organizationID) return
    let cancelled = false
    listBlueprints(organizationID)
      .then((r) => {
        if (cancelled) return
        if (r.error) {
          setLoadError(r.error)
          return
        }
        setRows(r.blueprints ?? [])
      })
      .catch((e) => {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : String(e))
      })
    return () => {
      cancelled = true
    }
  }, [organizationID])

  async function choose(row: Row, bindings = answers, parameters = paramAnswers) {
    if (!organizationID || busy) return
    setBusy(true)
    setError(null)
    try {
      const r = await materializeBlueprint(organizationID, row.slug, bindings, parameters)
      if (r.error) {
        setError(r.error)
        setBusy(false)
        return
      }
      if (r.provisioning_proposal) {
        popSheet()
        pushNav({ kind: 'onboarding_review', proposalID: r.provisioning_proposal.id })
        return
      }
      // Questions came back. Seed each one's default so a person can accept the
      // obvious answer without touching anything.
      const nextNeeds = r.needs ?? []
      const nextParams = r.parameters ?? []
      setChosen(row)
      setNeeds(nextNeeds)
      setParams(nextParams)
      setAnswers((prev) => {
        const next = { ...prev }
        for (const n of nextNeeds) {
          if (!next[n.alias] && n.options?.length === 1) next[n.alias] = n.options[0].id
        }
        return next
      })
      setParamAnswers((prev) => {
        const next = { ...prev }
        for (const p of nextParams) {
          if (next[p.name] === undefined) next[p.name] = p.default ?? ''
        }
        return next
      })
      setBusy(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      setBusy(false)
    }
  }

  const unanswerable = unanswerableNeeds(needs)
  const ready = allAnswered(needs, params, answers, paramAnswers)

  return (
    <div className="wizard wizard--sheet">
      <header className="wizard__nav">
        <button
          className="wizard__nav-btn"
          onClick={() => (chosen ? setChosen(null) : popSheet())}
        >
          {chosen ? '‹ Back' : 'Cancel'}
        </button>
        <div className="wizard__nav-title">
          {chosen ? chosen.display_name : 'Start from a pattern'}
        </div>
        <div className="wizard__nav-spacer" />
      </header>

      <div className="wizard__body">
        {!chosen && (
          <div className="wizard__step">
            <p className="wizard__hint">
              Roles other households have already worked out. You'll see exactly
              what each one sets up before anything happens.
            </p>

            {loadError && <p className="wizard__notice">{loadError}</p>}
            {!rows && !loadError && <p className="wizard__hint">Loading…</p>}
            {rows?.length === 0 && (
              <p className="wizard__notice">No patterns are published yet.</p>
            )}

            <ul className="wizard__list">
              {rows?.map((row) => (
                <li key={row.slug}>
                  <button
                    className="wizard__row blueprint__row"
                    disabled={busy}
                    onClick={() => void choose(row, {}, {})}
                  >
                    <span className="blueprint__name">{row.display_name}</span>
                    <span className="blueprint__summary">{row.summary}</span>
                  </button>
                </li>
              ))}
            </ul>
            {error && <p className="wizard__notice">{error}</p>}
          </div>
        )}

        {chosen && (
          <div className="wizard__step">
            {unanswerable.length > 0 && (
              <div className="blueprint__blocked" role="alert">
                {unanswerable.map((n) => (
                  <div key={n.alias}>
                    <strong>{n.prompt}</strong>
                    <p>You don't have one yet.</p>
                    {n.setup && <pre className="blueprint__setup">{n.setup.trim()}</pre>}
                  </div>
                ))}
              </div>
            )}

            {needs
              .filter((n) => (n.options?.length ?? 0) > 0)
              .map((need) => (
                <div key={need.alias} className="blueprint__field">
                  <label className="blueprint__label" htmlFor={`need-${need.alias}`}>
                    {need.prompt}
                  </label>
                  {need.guidance && (
                    <p className="blueprint__guidance">{need.guidance.trim()}</p>
                  )}
                  <select
                    id={`need-${need.alias}`}
                    className="blueprint__select"
                    value={answers[need.alias] ?? ''}
                    disabled={busy}
                    onChange={(e) =>
                      setAnswers((a) => ({ ...a, [need.alias]: e.target.value }))
                    }
                  >
                    <option value="">
                      {need.optional ? 'Skip this' : 'Choose…'}
                    </option>
                    {need.options.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              ))}

            {params.map((p) => (
              <div key={p.name} className="blueprint__field">
                <label className="blueprint__label" htmlFor={`param-${p.name}`}>
                  {p.prompt}
                </label>
                <input
                  id={`param-${p.name}`}
                  className="blueprint__input"
                  value={paramAnswers[p.name] ?? ''}
                  disabled={busy}
                  onChange={(e) =>
                    setParamAnswers((a) => ({ ...a, [p.name]: e.target.value }))
                  }
                />
              </div>
            ))}

            {error && <p className="wizard__notice">{error}</p>}

            <button
              className="wizard__primary"
              disabled={busy || !ready || unanswerable.length > 0}
              onClick={() => void choose(chosen)}
            >
              {busy ? 'Working…' : 'See what this sets up'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
